import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../shared/stores/toast.store';
import { describeApiError } from '../../shared/api/errors';
import { MoneyInput } from '../../shared/components/MoneyInput';
import { useAjusteManualRcMovimento, useComplementarRcMovimento } from './hooks';
import type { RcMovimentoTipo } from './types';

const OBS_MAX = 200;

export type MovimentoModo = 'complementar' | 'ajuste-manual';

export interface MovimentoLancamentoDialogProps {
  open: boolean;
  purchaseRequestId: number;
  modo: MovimentoModo;
  onClose: () => void;
}

/**
 * Lançamento de movimento na RC — Complementar (crédito fixo) ou Ajuste Manual
 * (crédito/débito). `valor` é magnitude positiva; no Ajuste o sinal vem do
 * seletor de tipo. Observação obrigatória (rule da Trn). Card [zn9Ds1NQ].
 */
export function MovimentoLancamentoDialog({
  open,
  purchaseRequestId,
  modo,
  onClose,
}: MovimentoLancamentoDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const complementar = useComplementarRcMovimento(purchaseRequestId);
  const ajuste = useAjusteManualRcMovimento(purchaseRequestId);

  const isAjuste = modo === 'ajuste-manual';
  const [valor, setValor] = useState('');
  const [tipo, setTipo] = useState<RcMovimentoTipo>('+');
  const [observacao, setObservacao] = useState('');
  const [touched, setTouched] = useState(false);

  const valorNum = Number(valor);
  const valorInvalido = !valor || !Number.isFinite(valorNum) || valorNum <= 0;
  const obsInvalida = observacao.trim().length === 0;
  const isPending = complementar.isPending || ajuste.isPending;

  function reset(): void {
    setValor('');
    setTipo('+');
    setObservacao('');
    setTouched(false);
  }

  async function handleSubmit(): Promise<void> {
    setTouched(true);
    if (valorInvalido || obsInvalida) return;
    try {
      if (isAjuste) {
        await ajuste.mutateAsync({ valor, tipo, observacao: observacao.trim() });
      } else {
        await complementar.mutateAsync({ valor, observacao: observacao.trim() });
      }
      toast.success(
        t(
          isAjuste
            ? 'features.requisicoesCompra.movimentos.ajuste.toastOk'
            : 'features.requisicoesCompra.movimentos.complementar.toastOk',
        ),
      );
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const prefix = isAjuste
    ? 'features.requisicoesCompra.movimentos.ajuste'
    : 'features.requisicoesCompra.movimentos.complementar';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: reset }}
    >
      <DialogTitle>{t(`${prefix}.title`)}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t(`${prefix}.descricao`)}
          </Typography>

          {isAjuste && (
            <ToggleButtonGroup
              exclusive
              color="primary"
              value={tipo}
              onChange={(_, v) => v && setTipo(v as RcMovimentoTipo)}
              size="small"
              aria-label={t('features.requisicoesCompra.movimentos.ajuste.tipoLabel')}
            >
              <ToggleButton value="+">
                {t('features.requisicoesCompra.movimentos.ajuste.credito')}
              </ToggleButton>
              <ToggleButton value="-">
                {t('features.requisicoesCompra.movimentos.ajuste.debito')}
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          <MoneyInput
            label={t('features.requisicoesCompra.movimentos.valorLabel')}
            value={valor}
            onChange={setValor}
            fullWidth
            error={touched && valorInvalido}
            helperText={
              touched && valorInvalido
                ? t('features.requisicoesCompra.movimentos.valorInvalido')
                : t('features.requisicoesCompra.movimentos.valorHelper')
            }
          />

          <TextField
            label={t('features.requisicoesCompra.movimentos.observacaoLabel')}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value.slice(0, OBS_MAX))}
            onBlur={() => setTouched(true)}
            multiline
            minRows={2}
            maxRows={5}
            fullWidth
            error={touched && obsInvalida}
            helperText={
              touched && obsInvalida
                ? t('features.requisicoesCompra.movimentos.observacaoRequired')
                : t('features.requisicoesCompra.movimentos.observacaoHelper', {
                    used: observacao.trim().length,
                    max: OBS_MAX,
                  })
            }
          />

          {isAjuste && tipo === '-' && (
            <Alert severity="info" variant="outlined">
              {t('features.requisicoesCompra.movimentos.ajuste.debitoAviso')}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color={isAjuste && tipo === '-' ? 'warning' : 'primary'}
          onClick={() => void handleSubmit()}
          disabled={isPending}
        >
          {isPending ? t('common.saving') : t(`${prefix}.submit`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
